import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { first } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { delay, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

import { DocumentViewerActions } from './document-viewer.actions';
import { DocumentViewerSelectors } from './document-viewer.selectors';
import { DocumentViewerViewMode } from './document-viewer.state';
import { environment } from '../../environments/environment';
import {
  getDocumentTransactionLibraryId,
  getTransactionIdFromDocumentTransactionLibraryId,
} from '../../features/document/constants/document-helper';
import {
  extendDocumentViewerUrl,
  getDocumentUrl,
} from '../../features/document-viewer/constants/get-document-viewer-url';
import {
  SemerisViewerCommand,
  ViewerCommand,
  XRayOptions,
} from '../../features/document-viewer/models/document-viewer.models';
import { DocumentViewerService } from '../../features/document-viewer/services/document-viewer.service';
import { SourceReference } from '../../features/shared/models/common/source-reference.model';
import { AggregatedLoggerService } from '../../features/logging/services/aggregated-logger.service';
import { UserProfileService } from '../../features/settings/services/user-profile.service';
import { FAIL_NOTIFICATION_CONFIGURATION } from '../../features/shared/constants/notification-configurations';
import { Permission } from '../../features/shared/models/api/permissions.enum';
import { DataService } from '../../features/shared/services/data.service';
import { DownloadService } from '../../features/shared/services/download.service';
import { LocalStorageService } from '../../features/shared/services/local-storage.service';
import { AppActions } from '../app/app.actions';
import { AppSelectors } from '../app/app.selectors';
import { DealActions } from '../deal/deal.actions';
import { RootState } from '../root-state.model';
import { TransactionSelectors } from '../transaction/transaction.selectors';
import { UserProfileSelectors } from '../user-profile/user-profile.selectors';
import { AuthService } from '@auth0/auth0-angular';
import { DocumentFormat } from '../../features/catalog/models/catalog.models';

@Injectable()
export class DocumentViewerEffects {
  private readonly logger = inject(AggregatedLoggerService);
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly downloadService = inject(DownloadService);
  private readonly dataService = inject(DataService);
  private readonly service = inject(DocumentViewerService);
  private readonly userProfileService = inject(UserProfileService);
  private readonly authService = inject(AuthService);

  sendSourceReferenceEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentViewerActions.SendCommand),
      withLatestFrom(
        this.store,
        this.store.select(TransactionSelectors.availableDocumentTransactionLibraryIdLookup),
        this.store.select(TransactionSelectors.documentHashLookup),
        this.store.select(TransactionSelectors.listLookup)
      ),
      switchMap(([action, store, availableDocumentTransactionLibraryIdLookup, documentHashLookup, listLookup]) =>
        of({
          action,
          store,
          availableDocumentTransactionLibraryIdLookup,
          documentHashLookup,
          listLookup,
        }).pipe(delay(action.delayMs || 0))
      ),
      switchMap(({ action, store, availableDocumentTransactionLibraryIdLookup, documentHashLookup, listLookup }) => {
        const currentDocumentTransactionLibraryId = store.documentViewer.documentTransactionLibraryId;
        const documentTransactionLibraryId =
          action.documentTransactionLibraryId || store.documentViewer.documentTransactionLibraryId;

        let requestedDocumentId: string;
        const requestedLibraryTransactionId: string =
          getTransactionIdFromDocumentTransactionLibraryId(documentTransactionLibraryId);

        requestedDocumentId = this.getDocumentIdByHash(
          action.message,
          requestedLibraryTransactionId,
          documentHashLookup
        );

        if (!requestedDocumentId) {
          requestedDocumentId = action.documentId;
        }

        let requestedDocumentTransactionLibraryId = getDocumentTransactionLibraryId(
          requestedDocumentId,
          requestedLibraryTransactionId
        );

        if (
          !!requestedDocumentTransactionLibraryId &&
          !availableDocumentTransactionLibraryIdLookup[requestedDocumentTransactionLibraryId]
        ) {
          // If the document cannot be found, try searching for a transaction that has only one document.
          let hasOnlyOneDocument = false;
          if (requestedLibraryTransactionId) {
            const transaction = listLookup[requestedLibraryTransactionId];

            if (transaction?.documents?.length === 1) {
              // Skip the document opening if the transaction has only one document, just send the command
              requestedDocumentTransactionLibraryId = getDocumentTransactionLibraryId(
                first(transaction.documents).documentId,
                transaction.transactionId
              );
              hasOnlyOneDocument = true;
            }
          }

          if (!hasOnlyOneDocument) {
            return of(
              AppActions.PushNotification({
                config: FAIL_NOTIFICATION_CONFIGURATION,
                message: 'Document not available',
              })
            );
          }
        }

        if (
          requestedDocumentTransactionLibraryId &&
          requestedDocumentTransactionLibraryId !== currentDocumentTransactionLibraryId
        ) {
          return of(
            DocumentViewerActions.OpenDocument({
              documentId: requestedDocumentId,
              initialCommand: action.message,
              documentTransactionLibraryId: requestedDocumentTransactionLibraryId,
            })
          );
        } else {
          return action.message.commandType !== ViewerCommand.SourceReference ||
            (!!action.message.value && !!action.message.value.length)
            ? this.service.postMessage(action.message).pipe(
                tap(() => {
                  this.logger.info(
                    `SemerisViewerClient: ${new Date().toISOString()} ${action.message.commandType} - ${action.message.value}`
                  );
                }),
                map(() => DocumentViewerActions.CommandSent())
              )
            : of(DocumentViewerActions.CommandSent());
        }
      })
    )
  );

  setLinkByTransactionEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentViewerActions.SetLinkByTransaction),
      map((action) =>
        DocumentViewerActions.SetLink({
          initialCommand: action.initialCommand,
          documentId: action.documentId,
          documentTransactionLibraryId: getDocumentTransactionLibraryId(
            action.documentId,
            action.transaction?.transactionId
          ),
          link: getDocumentUrl(environment.documentViewBaseUrl, action.transaction, action.documentId),
        })
      )
    )
  );

  setLinkEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentViewerActions.SetLink, DocumentViewerActions.SetLinkByTransaction),
      filter((action) => !!action.initialCommand),
      switchMap((action) => {
        return this.authService.getAccessTokenSilently().pipe(
          switchMap((token) => {
            localStorage.setItem('semeris-access-token', JSON.stringify(token));
            return of(DocumentViewerActions.SendCommand({ message: action.initialCommand }));
          })
        );
      })
    )
  );

  compareTurningOffEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.SetIsCompare),
      filter((action) => !action.isCompare),
      withLatestFrom(this.store, this.store.select(AppSelectors.isEmbedded)),
      map(([, store, isEmbedded]) => {
        const documentId = store.documentViewer.documentId;
        const transaction = isEmbedded ? store.embedded.deal : store.deal.transaction;

        if (!transaction) {
          return null;
        }

        const link = getDocumentUrl(environment.documentViewBaseUrl, transaction, documentId);
        this.service.requestOpen(transaction.transactionId);

        return link === store.documentViewer.link
          ? null
          : {
              link: getDocumentUrl(environment.documentViewBaseUrl, transaction, store.documentViewer.documentId),
              documentId: store.documentViewer.documentId,

              documentTransactionLibraryId: store.documentViewer.documentTransactionLibraryId,
            };
      }),
      filter((action) => !!action),
      map((action) => DocumentViewerActions.SetLink(action))
    )
  );

  loadInitDataEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.LoadInitDataSuccess),
      map((action) =>
        DocumentViewerActions.SetXRayOptions({
          options: action.data.userSettings?.xRayOptions as XRayOptions[],
        })
      )
    )
  );

  setXRayOptions$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentViewerActions.SetXRayOptions),
        withLatestFrom(this.store.select(UserProfileSelectors.permissions)),
        tap(([action, permissions]) => {
          const hasPermission = permissions?.includes(Permission.READ_XRAY);

          [XRayOptions.Entities, XRayOptions.LegalLogic, XRayOptions.Reflow].forEach((option) => {
            this.setOptionPersistence(option, hasPermission ? action.options?.includes(option) : false);
          });
        }),
        filter(([action]) => action.save),
        switchMap(([action]) => this.userProfileService.updateUserSettings({ xRayOptions: action.options }))
      ),
    { dispatch: false }
  );

  private getDocumentIdByHash(
    message: SemerisViewerCommand<SourceReference[]>,
    requestedLibraryTransactionId: string,
    documentHashLookup: Record<string, { documentId: string; transactionId: string }[]>
  ) {
    if (message.commandType === ViewerCommand.SourceReference) {
      const sourceRefs = message.value;
      const sourceRef = first(sourceRefs);
      const documentHash = sourceRef?.documentHash;

      if (documentHash && !!requestedLibraryTransactionId) {
        const availableDocumentIds = documentHashLookup[documentHash];
        const documentIdByHash = availableDocumentIds?.find(
          (id) => id.transactionId === requestedLibraryTransactionId
        )?.documentId;
        return documentIdByHash;
      }
    }

    return undefined;
  }

  private setOptionPersistence(option: XRayOptions, enabled: boolean) {
    LocalStorageService.set(enabled, `viewer-${option}`);
    this.service
      .postMessage({ commandType: option as any, value: enabled })
      .pipe(take(1))
      .subscribe();
  }

  openDocumentEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentViewerActions.OpenDocument),
      withLatestFrom(
        this.store,
        this.store.select(AppSelectors.isEmbedded),
        this.store.select(TransactionSelectors.listLookup)
      ),
      map(([action, store, isEmbedded, transactionLookup]) => {
        const customTransaction = transactionLookup[action.transactionId];
        const documentId = action.documentId || store.documentViewer.documentId;
        const transaction = customTransaction || (isEmbedded ? store.embedded.deal : store.deal.transaction);

        if (!transaction) {
          return null;
        }

        const link = getDocumentUrl(environment.documentViewBaseUrl, transaction, documentId);
        this.service.requestOpen(transaction.transactionId);

        if (link === store.documentViewer.link) {
          if (action.initialCommand) {
            return {
              message: action.initialCommand,
            };
          }

          return null;
        }

        return {
          link: getDocumentUrl(
            environment.documentViewBaseUrl,
            transaction,
            action.documentId || store.documentViewer.documentId
          ),
          documentId: action.documentId || store.documentViewer.documentId,
          initialCommand: action.initialCommand,
          documentTransactionLibraryId:
            action.documentTransactionLibraryId || store.documentViewer.documentTransactionLibraryId,
        };
      }),
      filter((action) => !!action),
      map((action) =>
        action.message
          ? DocumentViewerActions.SendCommand({ message: action.message })
          : DocumentViewerActions.SetLink(action as any)
      )
    )
  );

  openInNewTab$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentViewerActions.OpenInNewTab),
        withLatestFrom(
          this.store,
          this.store.select(DocumentViewerSelectors.transaction),
          this.store.select(UserProfileSelectors.permissions)
        ),
        map(([action, store, transaction, permissions]) =>
          window.open(extendDocumentViewerUrl(store.documentViewer, transaction, store.userProfile.theme, permissions))
        )
      ),
    { dispatch: false }
  );

  downloadDocumentEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DocumentViewerActions.DownloadDocument),
        withLatestFrom(this.store.select(DocumentViewerSelectors.originalDocumentFormat)),
        switchMap(([action, originalDocumentFormat]) => {
          const postfix = originalDocumentFormat === DocumentFormat.edgar ? 'html' : 'pdf';
          return this.dataService
            .getUrl(
              `docs/${action.deal.libraryId}/${action.deal.managerId}/${action.deal.transactionShortName}/${action.documentId}.${postfix}`
            )
            .pipe(
              tap(({ url }) => {
                this.downloadService.downloadFromUrl(url, `${action.documentId}.${postfix}`);
              })
            );
        })
      ),
    { dispatch: false }
  );

  setViewModeEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentViewerActions.SetViewMode),
      filter((action) => action.viewMode === DocumentViewerViewMode.Original),
      withLatestFrom(
        this.store.select(DocumentViewerSelectors.transaction),
        this.store.select(DocumentViewerSelectors.documentId),
        this.store.select(DocumentViewerSelectors.originalSignedUrl),
        this.store.select(DocumentViewerSelectors.originalTitle),
        this.store.select(DocumentViewerSelectors.originalDocumentFormat)
      ),
      switchMap(([action, transaction, documentId, pdfSignedUrl, pdfTitle, originalDocumentFormat]) => {
        return pdfSignedUrl
          ? of(
              DocumentViewerActions.SetCustomUrl({
                url: pdfSignedUrl,
                customTitle: pdfTitle,
                customFormat: originalDocumentFormat,
              })
            )
          : this.dataService
              .getUrl(
                `docs/${transaction.libraryId}/${transaction.managerId}/${transaction.transactionShortName}/${documentId}.${originalDocumentFormat === DocumentFormat.pdf ? 'pdf' : 'html'}`
              )
              .pipe(map(({ url }) => DocumentViewerActions.SetCustomUrl({ url: btoa(url) })));
      })
    )
  );
}
